import type { QueryClient } from '@tanstack/react-query';
import type { OnboardingStatus } from '@/types/organization';

/**
 * Sign-in reads the owner's first-run status to choose where to land; the owner area's first-run check (ProtectedRoute) then wants the same answer
 * a moment later. The answer is handed over through the query cache instead of being requested twice. Sign-in only hands over a status that is
 * completed or skipped, and on the server those only ever go from false to true, so such an answer cannot go out of date in a way that matters.
 * It is not consumed: every first-run check that mounts reuses it for up to a minute. It is also dropped as soon as it could be wrong: any write to the
 * server marks the cache stale (main.tsx), the first-run page drops it, and sign-out and sign-in empty the cache.
 */
const KEY = ['onboarding', 'status'] as const;
const FRESH_MS = 60_000;

export function handOffOnboarding(queryClient: QueryClient | undefined, onboarding: OnboardingStatus): void {
  queryClient?.setQueryData(KEY, onboarding);
}

/** The handed-over status (not removed: see above), or undefined when there is none, it is a minute old, or something was written since. */
export function handedOffOnboarding(queryClient: QueryClient | undefined): OnboardingStatus | undefined {
  const state = queryClient?.getQueryState<OnboardingStatus>(KEY);
  if (!state?.data || state.isInvalidated || Date.now() - state.dataUpdatedAt > FRESH_MS) return undefined;
  return state.data;
}

export function dropHandedOffOnboarding(queryClient: QueryClient | undefined): void {
  queryClient?.removeQueries({ queryKey: KEY });
}
